/**
 * APX WorkCenter — Google Drive Browser
 *
 * UX Flow (instant — no loading on first screen):
 *   1. A–Z letter grid                       → instantly rendered, no API call
 *   2. Company list                        → fetches only that letter's folder
 *   3. Folder / file drill-down            → fetches on demand
 */
import { useState, useRef, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Folder,
  FolderOpen,
  FolderPlus,
  FileText,
  FileImage,
  File,
  ChevronRight,
  ExternalLink,
  Search,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  HardDrive,
  ArrowLeft,
  Building2,
  Upload,
  Plus,
  X,
  Check,
} from "lucide-react";

// ─── Hardcoded A–Z folder IDs (fetched once, stable) ─────────────────────────
const LETTER_FOLDER_IDS: Record<string, string> = {
  "A": "1qutIMDl46DT35JM_1aw0ROR4h6CG4v_W",
  "B": "1iNxKd4a-ADuH675EJ52MR2qKNh4EyorG",
  "C": "1D18K3x1Gh-OEESQFnzmsLYhcfuxQHa2k",
  "D": "1SpdpdoB2BSHFMmwDUBA2qD_zuwn6pe1Q",
  "E": "1j_gQnRBUxg6cklAGyMNU2rfhQn6lqANm",
  "F": "1M-SW1Pc1ANEiFMV-kKGsFc4Tg3cfPd_Q",
  "G": "107DOk2R7PodQW63igbZ6N_TBm5vB-EK5",
  "H": "1lnGME4p5tvd_LPZWn1QYSPjmhREpxOSD",
  "I": "1FCapyz4fpz3MrJ1Eq5WmFyEuySOXtHNq",
  "J": "1X07Jqn1aAhG-IP3QmA3SANpaGLz5i8hD",
  "K": "1f4qWmCyldSWu6b7TgYBqmxey1b2edOIc",
  "L": "1JDwpaHoeehfr_rPDNcvqyWyrbg_irt5J",
  "M": "1Tak3cs4IOy5fCRBWkvcVh5_q2-i86DCO",
  "N": "1kW2FKN95NSh_e83AYgseN0qXus-M8XSL",
  "O": "17ok_bKKMgqr7qpubK8MyDxfO_VchLn9M",
  "P": "1Q0nLcjWTXArp0YtC50M7tM2P_aKq-0Z-",
  "Q": "1HEcZyNsXutOj33OuWKq7Uh5CjDvyOGS7",
  "R": "1k3zRaHt-6oFiVEDBnq8hDjE8cEKMfnDK",
  "S": "1uxDAaIofQMTVcQtGdYBpMj0ZRwREfV8-",
  "T": "19OpRSMUuQ-t7_f82uo8zuQCjleN4s87F",
  "U": "1tPam5HcnnXtGXrd-JqojUxpg5ozaGf6f",
  "V": "10RTGRCiv5toO9rog75KCG0wkh7dfLWCK",
  "W": "1oICy1Nc6_SbXz5PKF_g5nIENlGKHSgiV",
  "X": "1sogrnmosipJ4BjJfb2jXPe6K8pExTtQ_",
  "Y": "18-_nyFmSilWUWENbdqzSmpmWcvS8HNfG",
  "Z": "1yuHlNH6zqAo-z6ofo6OIl1F7HXwgjL8O",
};

const LETTERS = Object.keys(LETTER_FOLDER_IDS).sort();

const DRIVE_READONLY_EMAIL = "shobhit@apxsystem.io";

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime: string | null;
  size: string | null;
  webViewLink: string | null;
  iconLink: string | null;
  isFolder: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getFileIcon(mimeType: string) {
  if (mimeType.startsWith("image/")) return <FileImage className="w-4 h-4 text-[oklch(0.28_0.03_240)]" />;
  if (mimeType === "application/pdf") return <FileText className="w-4 h-4 text-[oklch(0.28_0.03_240)]" />;
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel"))
    return <FileText className="w-4 h-4 text-[oklch(0.28_0.03_240)]" />;
  if (mimeType.includes("document") || mimeType.includes("word"))
    return <FileText className="w-4 h-4 text-[oklch(0.28_0.03_240)]" />;
  return <File className="w-4 h-4 text-[oklch(0.28_0.03_240)]" />;
}

function formatFileSize(size: string | null): string {
  if (!size) return "";
  const bytes = parseInt(size, 10);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

// ─── Drive Auth Banner ────────────────────────────────────────────────────────
function DriveAuthBanner() {
  const { data: authData, isLoading } = trpc.drive.isAuthorized.useQuery();
  const { data: authUrlData } = trpc.drive.getAuthUrl.useQuery(undefined, {
    enabled: authData?.authorized === false,
  });
  if (isLoading || authData === undefined) return null;
  if (authData?.authorized) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[oklch(0.93_0.008_240)] border border-[oklch(0.82_0.02_240)] text-[oklch(0.08_0.01_240)] text-sm">
        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
        <span>Google Drive connected — apxadvisor@gmail.com</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-md bg-[oklch(0.93_0.008_240)] border border-[oklch(0.82_0.02_240)] text-[oklch(0.08_0.01_240)] text-sm">
      <AlertCircle className="w-4 h-4 flex-shrink-0" />
      <div className="flex-1">
        <span className="font-medium">Drive not connected.</span>{" "}
        Authorize apxadvisor@gmail.com to access ACCOUNTING FILES.
      </div>
      {authUrlData?.url && (
        <a href={authUrlData.url} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
          <Button size="sm" variant="outline" className="border-[oklch(0.78_0.02_240)] text-[oklch(0.08_0.01_240)] hover:bg-[oklch(0.90_0.008_240)]">
            Connect Drive
          </Button>
        </a>
      )}
    </div>
  );
}

// ─── Global Search Results ──────────────────────────────────────────────────
function GlobalSearchResults({
  query,
  onSelect,
}: {
  query: string;
  onSelect: (letter: string, company: DriveFile) => void;
}) {
  // Fetch all letters in parallel, filter client-side
  const queries = LETTERS.map((letter) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    trpc.drive.listClientFolders.useQuery(
      { letterFolderId: LETTER_FOLDER_IDS[letter] },
      { staleTime: 30 * 60_000 }
    )
  );

  const isLoading = queries.some((q) => q.isLoading);
  const q = query.toLowerCase().trim();

  const matches: { letter: string; company: DriveFile }[] = [];
  LETTERS.forEach((letter, i) => {
    (queries[i].data ?? []).forEach((company) => {
      if (company.name.toLowerCase().includes(q)) {
        matches.push({ letter, company });
      }
    });
  });

  if (isLoading && matches.length === 0) {
    return (
      <div className="flex items-center gap-2 py-4 text-[oklch(0.35_0.03_240)] text-sm">
        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
        Searching across all letters…
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div className="flex items-center gap-2 py-4 text-[oklch(0.35_0.03_240)] text-sm">
        <AlertCircle className="w-3.5 h-3.5" />
        No companies found matching &ldquo;{query}&rdquo;
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-[oklch(0.92_0.008_240)] overflow-hidden">
      {matches.slice(0, 20).map(({ letter, company }) => (
        <button
          key={company.id}
          onClick={() => onSelect(letter, company)}
          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[oklch(0.94_0.018_220)] transition-colors group text-left border-b border-[oklch(0.97_0.005_85)] last:border-0"
        >
          <span
            className="flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-sm font-bold"
            style={{ background: "oklch(0.97 0.01 220)", color: "oklch(0.08 0.01 240)" }}
          >
            {letter}
          </span>
          <Folder className="w-4 h-4 text-[oklch(0.28_0.03_240)] flex-shrink-0" />
          <span className="flex-1 text-sm text-[oklch(0.08_0.01_240)] group-hover:text-[oklch(0.08_0.01_240)] transition-colors truncate">
            {company.name}
          </span>
          <ChevronRight className="w-4 h-4 text-[oklch(0.28_0.03_240)] flex-shrink-0" />
        </button>
      ))}
      {matches.length > 20 && (
        <div className="px-4 py-2 text-sm text-[oklch(0.30_0.03_240)]">
          Showing 20 of {matches.length} results — refine your search
        </div>
      )}
    </div>
  );
}

// ─── Company List ─────────────────────────────────────────────────────────────
function CompanyList({
  letterFolderId,
  letter,
  searchQuery,
  onSelect,
}: {
  letterFolderId: string;
  letter: string;
  searchQuery: string;
  onSelect: (folder: DriveFile) => void;
}) {
  const { data, isLoading, error } = trpc.drive.listClientFolders.useQuery(
    { letterFolderId },
    { staleTime: 5 * 60_000 }
  );

  const companies = (data ?? []).filter((f) =>
    searchQuery.trim() ? f.name.toLowerCase().includes(searchQuery.toLowerCase()) : true
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-[oklch(0.35_0.03_240)]">
        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
        Loading companies under &ldquo;{letter}&rdquo;&hellip;
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 py-8 px-4 text-[oklch(0.08_0.01_240)] text-sm">
        <AlertCircle className="w-4 h-4" />
        Failed to load companies. Please try again.
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-[oklch(0.35_0.03_240)]">
        <Building2 className="w-8 h-8 mb-2 opacity-30" />
        <p className="text-sm">
          {searchQuery ? "No companies match your search" : `No companies found under "${letter}"`}
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-[oklch(0.97_0.005_85)/50]">
      {companies.map((company) => (
        <button
          key={company.id}
          onClick={() => onSelect(company)}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[oklch(0.94_0.018_220)] transition-colors group text-left"
        >
          <Folder className="w-4 h-4 text-[oklch(0.28_0.03_240)] flex-shrink-0" />
          <span className="flex-1 text-sm text-[oklch(0.08_0.01_240)] group-hover:text-[oklch(0.08_0.01_240)] transition-colors truncate font-medium">
            {company.name}
          </span>
          <ChevronRight className="w-4 h-4 text-[oklch(0.28_0.03_240)] group-hover:text-[oklch(0.08_0.01_240)] flex-shrink-0 transition-colors" />
        </button>
      ))}
      <div className="px-4 py-2.5 text-sm text-[oklch(0.30_0.03_240)]">
        {companies.length} {companies.length === 1 ? "company" : "companies"} under &ldquo;{letter}&rdquo;
      </div>
    </div>
  );
}

// ─── Folder Contents ──────────────────────────────────────────────────────────
function FolderContents({
  folderId,
  searchQuery,
  onNavigate,
  canWrite,
  uploaderEmail,
  uploaderName,
}: {
  folderId: string;
  searchQuery: string;
  onNavigate: (folder: DriveFile) => void;
  canWrite: boolean;
  uploaderEmail?: string;
  uploaderName?: string;
}) {
  const { data, isLoading, error, refetch } = trpc.drive.listFolderContents.useQuery(
    { folderId },
    { staleTime: 60_000 }
  );
  const utils = trpc.useUtils();

  // Drag-and-drop state
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dropFileInputRef = useRef<HTMLInputElement>(null);

  // Rename state
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const uploadMutation = trpc.drive.uploadFile.useMutation({
    onSuccess: () => { toast.success("File uploaded"); refetch(); },
    onError: (err) => toast.error(err.message),
  });
  const renameMutation = trpc.drive.renameItem.useMutation({
    onSuccess: (result) => { toast.success(`Renamed to "${result.name}"`); setRenamingId(null); refetch(); },
    onError: (err) => toast.error(err.message),
  });

  async function uploadFiles(files: File[]) {
    for (const file of files) {
      if (file.size > 16 * 1024 * 1024) { toast.error(`${file.name} exceeds 16 MB`); continue; }
      setUploading(true);
      try {
        const base64 = await toBase64(file);
        await uploadMutation.mutateAsync({
          folderId,
          fileName: file.name,
          mimeType: file.type || "application/octet-stream",
          fileBase64: base64,
          uploaderEmail,
          uploaderName,
        });
      } finally { setUploading(false); }
    }
    if (dropFileInputRef.current) dropFileInputRef.current.value = "";
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    if (!canWrite) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length) uploadFiles(files);
  }

  function startRename(f: DriveFile) {
    setRenamingId(f.id);
    setRenameValue(f.name);
  }

  async function commitRename() {
    if (!renamingId || !renameValue.trim()) return;
    await renameMutation.mutateAsync({ fileId: renamingId, newName: renameValue.trim(), callerEmail: uploaderEmail });
  }

  const items = (data ?? []).filter((f) =>
    searchQuery.trim() ? f.name.toLowerCase().includes(searchQuery.toLowerCase()) : true
  );

  const folders = items.filter((f) => f.isFolder);
  const files = items.filter((f) => !f.isFolder);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-[oklch(0.35_0.03_240)]">
        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
        Loading&hellip;
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 py-8 px-4 text-[oklch(0.08_0.01_240)] text-sm">
        <AlertCircle className="w-4 h-4" />
        Failed to load folder. Please try again.
      </div>
    );
  }

  return (
    <div
      className={`divide-y divide-[oklch(0.97_0.005_85)/50] ${isDragging && canWrite ? "ring-2 ring-inset ring-[oklch(0.72_0.14_75)] bg-[oklch(0.97_0.01_75)/30]" : ""}`}
      onDragOver={(e) => { e.preventDefault(); if (canWrite) setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
    >
      {items.length === 0 && !isDragging && (
        <div className="flex flex-col items-center justify-center py-10 text-[oklch(0.35_0.03_240)]">
          <Folder className="w-8 h-8 mb-2 opacity-30" />
          <p className="text-sm">{searchQuery ? "No results found" : "This folder is empty"}</p>
        </div>
      )}
      {folders.map((f) => (
        <div key={f.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[oklch(0.94_0.018_220)] transition-colors group">
          {renamingId === f.id ? (
            <>
              <FolderOpen className="w-4 h-4 text-[oklch(0.28_0.03_240)] flex-shrink-0" />
              <Input
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenamingId(null); }}
                className="flex-1 h-7 text-sm bg-white border-[oklch(0.82_0.02_240)] text-[oklch(0.08_0.01_240)]"
              />
              <Button size="sm" onClick={commitRename} disabled={!renameValue.trim() || renameMutation.isPending} className="h-7 px-2 bg-[oklch(0.12_0.04_240)] text-white">
                {renameMutation.isPending ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setRenamingId(null)} className="h-7 w-7 p-0">
                <X className="w-3 h-3" />
              </Button>
            </>
          ) : (
            <>
              <button onClick={() => onNavigate(f)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                <FolderOpen className="w-4 h-4 text-[oklch(0.28_0.03_240)] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[oklch(0.08_0.01_240)] truncate font-medium">{f.name}</p>
                  {f.modifiedTime && <p className="text-sm text-[oklch(0.35_0.03_240)]">{formatDate(f.modifiedTime)}</p>}
                </div>
                <ChevronRight className="w-4 h-4 text-[oklch(0.28_0.03_240)] group-hover:text-[oklch(0.08_0.01_240)] flex-shrink-0 transition-colors" />
              </button>
              {canWrite && (
                <Button
                  size="sm" variant="ghost"
                  onClick={() => startRename(f)}
                  title="Rename folder"
                  className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </Button>
              )}
            </>
          )}
        </div>
      ))}
      {files.map((f) => (
        <div key={f.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[oklch(0.93_0.008_240)/50] group">
          <div className="flex-shrink-0">{getFileIcon(f.mimeType)}</div>
          {renamingId === f.id ? (
            <>
              <Input
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenamingId(null); }}
                className="flex-1 h-7 text-sm bg-white border-[oklch(0.82_0.02_240)] text-[oklch(0.08_0.01_240)]"
              />
              <Button size="sm" onClick={commitRename} disabled={!renameValue.trim() || renameMutation.isPending} className="h-7 px-2 bg-[oklch(0.12_0.04_240)] text-white">
                {renameMutation.isPending ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setRenamingId(null)} className="h-7 w-7 p-0">
                <X className="w-3 h-3" />
              </Button>
            </>
          ) : (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[oklch(0.18_0.04_240)] truncate">{f.name}</p>
                {f.modifiedTime && <p className="text-sm text-[oklch(0.35_0.03_240)]">{formatDate(f.modifiedTime)}</p>}
              </div>
              {f.size && <span className="text-sm text-[oklch(0.35_0.03_240)] flex-shrink-0">{formatFileSize(f.size)}</span>}
              {f.webViewLink && (
                <a href={f.webViewLink} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-sm text-[oklch(0.08_0.01_240)] hover:bg-[oklch(0.90_0.008_240)]">
                    <ExternalLink className="w-3.5 h-3.5 mr-1" />Open
                  </Button>
                </a>
              )}
              {canWrite && (
                <Button
                  size="sm" variant="ghost"
                  onClick={() => startRename(f)}
                  title="Rename file"
                  className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </Button>
              )}
            </>
          )}
        </div>
      ))}
      {/* Drag-and-drop zone */}
      {canWrite && (
        <div
          className={`mx-4 my-3 rounded-lg border-2 border-dashed transition-colors cursor-pointer ${
            isDragging
              ? "border-[oklch(0.72_0.14_75)] bg-[oklch(0.97_0.01_75)/50]"
              : "border-[oklch(0.82_0.02_240)] hover:border-[oklch(0.55_0.08_240)] hover:bg-[oklch(0.95_0.005_240)]"
          }`}
          onClick={() => dropFileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center justify-center py-4 gap-1.5 pointer-events-none">
            {uploading ? (
              <RefreshCw className="w-5 h-5 animate-spin text-[oklch(0.35_0.03_240)]" />
            ) : (
              <Upload className="w-5 h-5 text-[oklch(0.35_0.03_240)]" />
            )}
            <p className="text-sm text-[oklch(0.35_0.03_240)]">
              {uploading ? "Uploading…" : "Drag & drop files here, or click to upload"}
            </p>
          </div>
          <input ref={dropFileInputRef} type="file" multiple className="hidden" onChange={(e) => uploadFiles(Array.from(e.target.files ?? []))} />
        </div>
      )}
      <div className="px-4 py-2.5 text-sm text-[oklch(0.30_0.03_240)]">
        {folders.length > 0 && `${folders.length} folder${folders.length !== 1 ? "s" : ""}`}
        {folders.length > 0 && files.length > 0 && " · "}
        {files.length > 0 && `${files.length} file${files.length !== 1 ? "s" : ""}`}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function GoogleDrive() {
  const { user } = useAuth();
  const canWrite = user?.email?.toLowerCase() !== DRIVE_READONLY_EMAIL;
  const [selectedLetter, setSelectedLetter] = useState<string>("");
  const [selectedCompany, setSelectedCompany] = useState<DriveFile | null>(null);
  const [folderStack, setFolderStack] = useState<DriveFile[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  // Level 2: New Client Folder state
  const [showNewClientFolder, setShowNewClientFolder] = useState(false);
  const [newClientFolderName, setNewClientFolderName] = useState("");
  const [creatingClientFolder, setCreatingClientFolder] = useState(false);
  // Level 3: New Folder + Upload state
  const [showNewSubFolder, setShowNewSubFolder] = useState(false);
  const [newSubFolderName, setNewSubFolderName] = useState("");
  const [creatingSubFolder, setCreatingSubFolder] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const utils = trpc.useUtils();
  // Write mutations
  const createFolderMutation = trpc.drive.createFolder.useMutation({
    onSuccess: (result) => {
      toast.success(`Folder "${result.name}" created`);
      setShowNewClientFolder(false);
      setNewClientFolderName("");
      setShowNewSubFolder(false);
      setNewSubFolderName("");
      utils.drive.listClientFolders.invalidate();
      utils.drive.listFolderContents.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });
  const uploadMutation = trpc.drive.uploadFile.useMutation({
    onSuccess: () => {
      toast.success("File uploaded successfully");
      utils.drive.listFolderContents.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  async function handleCreateClientFolder() {
    if (!newClientFolderName.trim() || !letterFolderId) return;
    setCreatingClientFolder(true);
    try {
      await createFolderMutation.mutateAsync({
        parentFolderId: letterFolderId,
        folderName: newClientFolderName.trim().toUpperCase(),
        uploaderEmail: user?.email ?? undefined,
      });
    } finally {
      setCreatingClientFolder(false);
    }
  }

  async function handleCreateSubFolder() {
    if (!newSubFolderName.trim() || !currentFolderId) return;
    setCreatingSubFolder(true);
    try {
      await createFolderMutation.mutateAsync({
        parentFolderId: currentFolderId,
        folderName: newSubFolderName.trim(),
        uploaderEmail: user?.email ?? undefined,
      });
    } finally {
      setCreatingSubFolder(false);
    }
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (!files.length || !currentFolderId) return;
    for (const file of files) {
      if (file.size > 16 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 16 MB limit`);
        continue;
      }
      setUploading(true);
      try {
        const base64 = await toBase64(file);
        await uploadMutation.mutateAsync({
          folderId: currentFolderId,
          fileName: file.name,
          mimeType: file.type || "application/octet-stream",
          fileBase64: base64,
          uploaderEmail: user?.email ?? undefined,
          uploaderName: user?.name ?? undefined,
        });
      } finally {
        setUploading(false);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  // Fetch letter counts from DB cache (instant — no Drive API call on page load)
  const { data: letterCounts } = trpc.drive.cachedLetterCounts.useQuery(undefined, {
    staleTime: 5 * 60_000, // re-check every 5 min; nightly scheduler keeps DB fresh
  });
  // Derived
  const level: "letters" | "companies" | "folder" =
    folderStack.length > 0 || selectedCompany
      ? "folder"
      : selectedLetter
      ? "companies"
      : "letters";

  const currentFolderId: string | null =
    folderStack.length > 0
      ? folderStack[folderStack.length - 1].id
      : selectedCompany?.id ?? null;

  const letterFolderId = selectedLetter ? LETTER_FOLDER_IDS[selectedLetter] : null;

  // ── Navigation ──
  function selectLetter(letter: string) {
    setSelectedLetter(letter);
    setSelectedCompany(null);
    setFolderStack([]);
    setSearchQuery("");
  }

  function selectCompany(company: DriveFile) {
    setSelectedCompany(company);
    setFolderStack([]);
    setSearchQuery("");
  }

  function enterFolder(folder: DriveFile) {
    setFolderStack((prev) => [...prev, folder]);
    setSearchQuery("");
  }

  function goBack() {
    if (folderStack.length > 0) {
      setFolderStack((prev) => prev.slice(0, -1));
    } else if (selectedCompany) {
      setSelectedCompany(null);
    } else if (selectedLetter) {
      setSelectedLetter("");
    }
    setSearchQuery("");
  }

  // ── Breadcrumb ──
  type Crumb = { label: string; onClick: () => void };
  const crumbs: Crumb[] = [
    {
      label: "ACCOUNTING FILES",
      onClick: () => {
        setSelectedLetter("");
        setSelectedCompany(null);
        setFolderStack([]);
        setSearchQuery("");
      },
    },
  ];
  if (selectedLetter) {
    crumbs.push({
      label: selectedLetter,
      onClick: () => {
        setSelectedCompany(null);
        setFolderStack([]);
        setSearchQuery("");
      },
    });
  }
  if (selectedCompany) {
    crumbs.push({
      label: selectedCompany.name,
      onClick: () => {
        setFolderStack([]);
        setSearchQuery("");
      },
    });
  }
  folderStack.forEach((f, i) => {
    crumbs.push({
      label: f.name,
      onClick: () => {
        setFolderStack((prev) => prev.slice(0, i + 1));
        setSearchQuery("");
      },
    });
  });

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl mx-auto space-y-5">

        {/* ── Page Header ── */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <HardDrive className="w-6 h-6 text-[oklch(0.28_0.03_240)]" />
            <div>
              <h1 className="text-xl font-semibold text-[oklch(0.08_0.01_240)]">Google Drive</h1>
              <p className="text-sm text-[oklch(0.35_0.03_240)]">APX ACCOUNTING INFO / ACCOUNTING FILES</p>
            </div>
          </div>
          {level !== "letters" && (
            <Button
              variant="outline"
              size="sm"
              onClick={goBack}
              className="border-[oklch(0.3_0.04_240)] text-[oklch(0.08_0.01_240)] hover:bg-[oklch(0.94_0.018_220)]"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Back
            </Button>
          )}
        </div>

        {/* ── Drive Auth Banner ── */}
        <DriveAuthBanner />

        {/* ── Breadcrumb ── */}
        <nav className="flex items-center gap-1 text-sm flex-wrap">
          {crumbs.map((crumb, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-[oklch(0.28_0.03_240)]" />}
              {i < crumbs.length - 1 ? (
                <button
                  onClick={crumb.onClick}
                  className="text-[oklch(0.08_0.01_240)] hover:underline transition-colors"
                >
                  {crumb.label}
                </button>
              ) : (
                <span className="text-[oklch(0.28_0.04_240)] font-semibold">{crumb.label}</span>
              )}
            </span>
          ))}
        </nav>

        {/* ── Main Content Card ── */}
        <Card className="bg-[oklch(0.97_0.005_240)] border-[oklch(0.82_0.02_240)] overflow-hidden">
          <CardContent className="p-0">

            {/* ── Level 1: A-Z Letter grid (instant, no API call) ── */}
            {level === "letters" && (
              <div className="p-5 space-y-4">
                {/* Global search bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[oklch(0.28_0.03_240)]" />
                  <Input
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                    placeholder="Search any company name directly…"
                    className="pl-9 bg-[oklch(0.93_0.008_240)] border-[oklch(0.88_0.01_240)] text-[oklch(0.08_0.01_240)] placeholder:text-[oklch(0.28_0.03_240)] focus:border-[oklch(0.12_0.04_240)]"
                  />
                </div>
                {/* Global search results */}
                {globalSearch.trim().length >= 2 && (
                  <GlobalSearchResults
                    query={globalSearch}
                    onSelect={(letter, company) => {
                      setGlobalSearch("");
                      selectLetter(letter);
                      selectCompany(company);
                    }}
                  />
                )}
                {/* A-Z grid — only shown when not searching */}
                {!globalSearch.trim() && (
                  <>
                    <p className="text-sm text-[oklch(0.08_0.01_240)] uppercase tracking-widest font-medium">
                      Or select a letter to browse clients
                    </p>
                    <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(52px, 1fr))" }}>
                      {LETTERS.map((letter) => {
                        const count = letterCounts?.[letter];
                        return (
                          <button
                            key={letter}
                            onClick={() => selectLetter(letter)}
                            className="relative flex flex-col items-center justify-center rounded-lg transition-all duration-150 hover:scale-105 active:scale-95"
                            style={{
                              height: "48px",
                              background: "oklch(0.92 0.008 240)",
                              border: "1px solid oklch(0.78 0.02 240)",
                              color: "oklch(0.08 0.01 240)",
                            }}
                            onMouseEnter={(e) => {
                              const btn = e.currentTarget as HTMLButtonElement;
                              btn.style.background = "oklch(0.12 0.04 240)";
                              btn.style.borderColor = "oklch(0.12 0.04 240)";
                              btn.style.color = "oklch(0.97 0.005 240)";
                            }}
                            onMouseLeave={(e) => {
                              const btn = e.currentTarget as HTMLButtonElement;
                              btn.style.background = "oklch(0.92 0.008 240)";
                              btn.style.borderColor = "oklch(0.78 0.02 240)";
                              btn.style.color = "oklch(0.08 0.01 240)";
                            }}
                          >
                            <span className="text-sm font-bold leading-none">{letter}</span>
                            {count !== undefined && (
                              <span
                                className="text-[9px] leading-none mt-0.5 font-medium"
                                style={{ color: "oklch(0.08 0.01 240)" }}
                              >
                                {count}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-sm text-[oklch(0.28_0.03_240)]">
                      Click a letter to see all client companies. Counts load in the background.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* ── Level 2: Company list ── */}
            {level === "companies" && letterFolderId && (
              <>
                <div className="px-4 pt-4 pb-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[oklch(0.28_0.03_240)]" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={`Search companies starting with "${selectedLetter}"…`}
                        className="pl-9 bg-[oklch(0.93_0.008_240)] border-[oklch(0.88_0.01_240)] text-[oklch(0.18_0.04_240)] placeholder:text-[oklch(0.28_0.03_240)]"
                      />
                    </div>
                    {canWrite && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setShowNewClientFolder((v) => !v); setNewClientFolderName(""); }}
                        className="flex-shrink-0 border-[oklch(0.3_0.04_240)] text-[oklch(0.08_0.01_240)] hover:bg-[oklch(0.94_0.018_220)] gap-1.5"
                      >
                        <FolderPlus className="w-3.5 h-3.5" />
                        + New Client Folder
                      </Button>
                    )}
                  </div>
                  {showNewClientFolder && canWrite && (
                    <div className="flex items-center gap-2 p-3 rounded-lg border border-dashed border-[oklch(0.72_0.14_75)] bg-[oklch(0.97_0.01_75)/40]">
                      <FolderPlus className="w-4 h-4 text-[oklch(0.55_0.12_75)] flex-shrink-0" />
                      <Input
                        autoFocus
                        value={newClientFolderName}
                        onChange={(e) => setNewClientFolderName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleCreateClientFolder(); if (e.key === "Escape") setShowNewClientFolder(false); }}
                        placeholder={`New client folder name under "${selectedLetter}" (e.g. ALPHA ROOFING INC)…`}
                        className="flex-1 bg-white border-[oklch(0.82_0.02_240)] text-[oklch(0.08_0.01_240)] text-sm"
                      />
                      <Button
                        size="sm"
                        onClick={handleCreateClientFolder}
                        disabled={!newClientFolderName.trim() || creatingClientFolder}
                        className="flex-shrink-0 bg-[oklch(0.12_0.04_240)] text-white hover:bg-[oklch(0.18_0.04_240)] gap-1"
                      >
                        {creatingClientFolder ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        Create
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowNewClientFolder(false)} className="flex-shrink-0 h-8 w-8 p-0">
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
                <CompanyList
                  letterFolderId={letterFolderId}
                  letter={selectedLetter}
                  searchQuery={searchQuery}
                  onSelect={selectCompany}
                />
              </>
            )}

            {/* ── Level 3+: Folder contents ── */}
            {level === "folder" && currentFolderId && (
              <>
                <div className="px-4 pt-4 pb-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[oklch(0.28_0.03_240)]" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search in this folder…"
                        className="pl-9 bg-[oklch(0.93_0.008_240)] border-[oklch(0.88_0.01_240)] text-[oklch(0.18_0.04_240)] placeholder:text-[oklch(0.28_0.03_240)]"
                      />
                    </div>
                    {canWrite && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setShowNewSubFolder((v) => !v); setNewSubFolderName(""); }}
                          className="flex-shrink-0 border-[oklch(0.3_0.04_240)] text-[oklch(0.08_0.01_240)] hover:bg-[oklch(0.94_0.018_220)] gap-1.5"
                        >
                          <FolderPlus className="w-3.5 h-3.5" />
                          New Folder
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          className="flex-shrink-0 border-[oklch(0.3_0.04_240)] text-[oklch(0.08_0.01_240)] hover:bg-[oklch(0.94_0.018_220)] gap-1.5"
                        >
                          {uploading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                          Upload File
                        </Button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          className="hidden"
                          onChange={handleFileUpload}
                        />
                      </>
                    )}
                  </div>
                  {showNewSubFolder && canWrite && (
                    <div className="flex items-center gap-2 p-3 rounded-lg border border-dashed border-[oklch(0.72_0.14_75)] bg-[oklch(0.97_0.01_75)/40]">
                      <FolderPlus className="w-4 h-4 text-[oklch(0.55_0.12_75)] flex-shrink-0" />
                      <Input
                        autoFocus
                        value={newSubFolderName}
                        onChange={(e) => setNewSubFolderName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleCreateSubFolder(); if (e.key === "Escape") setShowNewSubFolder(false); }}
                        placeholder="New folder name (e.g. 2027 or MISC DOCS)…"
                        className="flex-1 bg-white border-[oklch(0.82_0.02_240)] text-[oklch(0.08_0.01_240)] text-sm"
                      />
                      <Button
                        size="sm"
                        onClick={handleCreateSubFolder}
                        disabled={!newSubFolderName.trim() || creatingSubFolder}
                        className="flex-shrink-0 bg-[oklch(0.12_0.04_240)] text-white hover:bg-[oklch(0.18_0.04_240)] gap-1"
                      >
                        {creatingSubFolder ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        Create
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowNewSubFolder(false)} className="flex-shrink-0 h-8 w-8 p-0">
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
                <FolderContents
                  folderId={currentFolderId}
                  searchQuery={searchQuery}
                  onNavigate={enterFolder}
                  canWrite={canWrite}
                  uploaderEmail={user?.email ?? undefined}
                  uploaderName={user?.name ?? undefined}
                />
              </>
            )}

          </CardContent>
        </Card>

      </div>
    </DashboardLayout>
  );
}
